import React, { useEffect, useRef, useState } from 'react';
import {
    Edit3,
    Inbox,
    Send,
    FileText,
    Trash2,
    ShieldAlert,
    Folder,
    MoreHorizontal,
} from 'lucide-react';
import './MessagesIconRail.css';

/* ==================================================
   KUDDLECORE DEVELOPMENT STANDARD

   Purpose:
   Message navigation rail.

   UI Standards:
   • Every interactive button includes:
       - title
       - aria-label

   • Every dropdown button includes:
       - aria-haspopup
       - aria-expanded

   • Dropdown selections automatically
     close the menu after completion.

   ================================================== */

const MessagesIconRail = ({
    activeTab,
    setActiveTab,
    onCompose,
    onAddFolder,
    onEmptyTrash,
    onRestoreTrash,
}) => {
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [showTrashMenu, setShowTrashMenu] = useState(false);

    const moreRef = useRef(null);
    const trashRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (moreRef.current && !moreRef.current.contains(e.target)) {
                setShowMoreMenu(false);
            }

            if (trashRef.current && !trashRef.current.contains(e.target)) {
                setShowTrashMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const railItems = [
        {
            key: 'inbox',
            label: 'Inbox',
            tooltip: 'View Inbox',
            icon: <Inbox size={22} />,
        },
        {
            key: 'sent',
            label: 'Sent',
            tooltip: 'View Sent Messages',
            icon: <Send size={22} />,
        },
        {
            key: 'drafts',
            label: 'Drafts',
            tooltip: 'View Draft Messages',
            icon: <FileText size={22} />,
        },
        {
            key: 'spam',
            label: 'Spam',
            tooltip: 'View Spam Messages',
            icon: <ShieldAlert size={22} />,
        },
    ];

    return (
        <aside className="messages-icon-rail" aria-label="Messages navigation">
            <button
                type="button"
                className="rail-compose-btn"
                title="Compose Message"
                aria-label="Compose Message"
                onClick={onCompose}
            >
                <Edit3 size={24} />
            </button>

            <nav
                className="rail-nav"
                aria-label="Message folders"
            >

                {/* ==================================================
    Primary Navigation
================================================== */}
                {railItems.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        className={`rail-item ${activeTab?.toLowerCase() === item.key ? 'active' : ''
                            }`}
                        onClick={() => setActiveTab(item.key)}
                        title={item.tooltip}
                        aria-label={item.tooltip}
                    >
                        <span className="rail-icon">{item.icon}</span>
                        <span className="rail-label">{item.label}</span>
                    </button>
                ))}

                {/* ==================================================
    Trash Menu
================================================== */}
                <div className="rail-menu-shell" ref={trashRef}>
                    <button
                        type="button"
                        className={`rail-item ${activeTab?.toLowerCase() === 'trash' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('trash');
                            setShowTrashMenu((prev) => !prev);
                        }}
                        title="View Trash"
                        aria-label="View Trash"
                        aria-haspopup="menu"
                        aria-expanded={showTrashMenu}
                    >
                        <span className="rail-icon"><Trash2 size={22} /></span>
                        <span className="rail-label">Trash</span>
                    </button>

                    {showTrashMenu && (
                        <div className="rail-dropdown">
                            <button
                                type="button"
                                title="Open Trash Folder"
                                aria-label="Open Trash Folder"
                                onClick={() => {
                                    setActiveTab('trash');
                                    setShowTrashMenu(false);
                                }}
                            >
                                Trash
                            </button>

                            <button
                                type="button"
                                title="Restore Selected Items"
                                aria-label="Restore Selected Items"
                                onClick={() => {
                                    onRestoreTrash();
                                    setShowTrashMenu(false);
                                }}
                            >
                                Restore Trash
                            </button>

                            <button
                                type="button"
                                title="Permanently Delete All Trash"
                                aria-label="Permanently Delete All Trash"
                                onClick={() => {
                                    onEmptyTrash();
                                    setShowTrashMenu(false);
                                }}
                            >
                                Empty Trash
                            </button>
                        </div>
                    )}
                </div>

                {/* ==================================================
    Folder Management
================================================== */}
                <button
                    type="button"
                    className={`rail-item ${activeTab?.toLowerCase() === 'folders' ? 'active' : ''}`}
                    onClick={onAddFolder}
                    title="Manage Folders"
                    aria-label="Manage Folders"
                >
                    <span className="rail-icon"><Folder size={22} /></span>
                    <span className="rail-label">Folders</span>
                </button>

                {/* ==================================================
    More Menu
================================================== */}
                <div className="rail-menu-shell" ref={moreRef}>
                    <button
                        type="button"
                        className="rail-item"
                        onClick={() => setShowMoreMenu((prev) => !prev)}
                        title="More Message Options"
                        aria-label="More Message Options"
                        aria-haspopup="menu"
                        aria-expanded={showMoreMenu}
                    >
                        <span className="rail-icon"><MoreHorizontal size={23} /></span>
                        <span className="rail-label">More</span>
                    </button>

                    {showMoreMenu && (
                        <div className="rail-dropdown">
                            <button
                                type="button"
                                title="View Unread Messages"
                                aria-label="View Unread Messages"
                                onClick={() => {
                                    setActiveTab('unread');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Unread
                            </button>

                            <button
                                type="button"
                                title="View Scheduled Messages"
                                aria-label="View Scheduled Messages"
                                onClick={() => {
                                    setActiveTab('scheduled');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Scheduled
                            </button>

                            <button
                                type="button"
                                title="View Messages with Attachments"
                                aria-label="View Messages with Attachments"
                                onClick={() => {
                                    setActiveTab('attachments');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Attachments
                            </button>

                            <button
                                type="button"
                                title="View Archived Messages"
                                aria-label="View Archived Messages"
                                onClick={() => {
                                    setActiveTab('archive');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Archive
                            </button>

                            <button
                                type="button"
                                title="View Messages Sent to Yourself"
                                aria-label="View Messages Sent to Yourself"
                                onClick={() => {
                                    setActiveTab('self');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Emails to Myself
                            </button>

                            <button
                                type="button"
                                title="Open Message Settings"
                                aria-label="Open Message Settings"
                                onClick={() => {
                                    setActiveTab('settings');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Settings
                            </button>

                            <button
                                type="button"
                                title="Open Help Center"
                                aria-label="Open Help Center"
                                onClick={() => {
                                    setActiveTab('help');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Help
                            </button>

                            <button
                                type="button"
                                title="Send Feedback"
                                aria-label="Send Feedback"
                                onClick={() => {
                                    setActiveTab('feedback');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Give Feedback
                            </button>

                            <button
                                type="button"
                                title="View Keyboard Shortcuts"
                                aria-label="View Keyboard Shortcuts"
                                onClick={() => {
                                    setActiveTab('shortcuts');
                                    setShowMoreMenu(false);
                                }}
                            >
                                Keyboard Shortcuts
                            </button>
                        </div>
                    )}
                </div>
            </nav>
        </aside>
    );
};

export default MessagesIconRail;